import { faBed, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  getCancellationPolicyInfo,
  getTotalPriceText,
  Icon,
  IconName,
  twoDecimalFormatter,
  useDeviceTypes,
  SanitizedHtml,
} from "halifax";
import React from "react";
import {
  BookedRooms,
  CancellationPolicyEnum,
  HotelCancellationPolicy,
  HotelCancellationPolicyV2,
  HotelCfarContract,
  LodgingAvailabilityCheckInInstructions,
  RoomProduct,
} from "redmond";
import { isCorpTenant, useMultiroomCountText } from "@capone/common";
import dayjs from "dayjs";
import {
  CFAR_CANCELLATION_SECONDARY_TEXT_REFUND_AMOUNT,
  PREMIER_COLLECTION_BENEFITS_LIST,
  PREMIER_COLLECTION_BENEFITS_TITLE,
  ROOM_TYPE,
  CHECK_IN_INSTRUCTIONS_TITLE,
  formattedCheckIn,
  formattedCheckOut,
  CFAR_CANCELLATION_PRIMARY,
  CFAR_NEGATIVE_PREMIUM_SECONDARY_TEXT,
} from "./constants";
import "./styles.scss";
import { config } from "../../../../../../../../api/config";
import {
  ActiveExperiments,
  useExperiment,
} from "../../../../../../../../context/experiments";
import { HotelCancellationPolicyV2Enum } from "redmond/build";

interface ICancellationText {
  primaryText: string;
  secondaryText?: string;
  iconName?: string;
}

const getCancellationText = ({
  cancellationPolicy,
  cancellationPolicyV2,
  hotelCfar,
  refundAmount,
  cancellationPolicyExperimentEnabled,
}: {
  cancellationPolicy: HotelCancellationPolicy;
  cancellationPolicyV2: HotelCancellationPolicyV2;
  hotelCfar?: HotelCfarContract;
  refundAmount: string;
  cancellationPolicyExperimentEnabled: boolean;
}): ICancellationText => {
  if (hotelCfar) {
    const { shouldRefundCfarPremium, coverage, premium, expired } = hotelCfar;
    const isNegativePremium = premium.amount.fiat.value < 0;
    const formattedExpirationDate = dayjs(expired).format("MMM D");
    const formattedCoverageAmount = getTotalPriceText({
      price: coverage.amount.fiat,
      priceFormatter: twoDecimalFormatter,
    });

    return {
      iconName: IconName.CheckShieldBlue,
      primaryText: CFAR_CANCELLATION_PRIMARY,
      secondaryText: isNegativePremium
        ? CFAR_NEGATIVE_PREMIUM_SECONDARY_TEXT(
            formattedExpirationDate,
            coverage.percentage,
            formattedCoverageAmount
          )
        : CFAR_CANCELLATION_SECONDARY_TEXT_REFUND_AMOUNT(
            refundAmount,
            !!shouldRefundCfarPremium
          ),
    };
  }

  if (cancellationPolicyExperimentEnabled) {
    if (
      cancellationPolicyV2?.CancellationPolicyV2 ===
      HotelCancellationPolicyV2Enum.Unknown
    ) {
      return { primaryText: cancellationPolicy.primaryText };
    } else {
      return {
        primaryText: cancellationPolicyV2.primaryText,
        secondaryText: cancellationPolicyV2.secondaryText,
      };
    }
  } else {
    if (
      cancellationPolicy?.CancellationPolicy === CancellationPolicyEnum.Cfar
    ) {
      return { primaryText: cancellationPolicy.primaryText };
    } else {
      return {
        primaryText: cancellationPolicy.primaryText,
        secondaryText: cancellationPolicy.secondaryText,
      };
    }
  }
};

export interface IHotelShopRoomTypePickerProps {
  bookedRooms: BookedRooms[];
  cancellationPolicy?: HotelCancellationPolicy;
  isLuxuryCollection?: boolean;
}

export interface IHotelShopRoomTypePickerState {
  panelExpanded: boolean;
}

// TODO: revisit this component once we have a better understanding on how to handle globalization;
// some texts will not likely be translatable simply by switching nightText alone (because of grammar differences)
export interface HotelShopInfoPartsTexts {
  nightText?: string;
  perNightText?: string;
}

export const RoomRefundability = ({
  product,
}: {
  product: RoomProduct;
}): React.ReactElement | null => {
  const cancellationClassAndTitle = getCancellationPolicyInfo(
    product.cancellationPolicy
  );

  if (!cancellationClassAndTitle) {
    return null;
  }

  return (
    <Box
      className={clsx(
        "hotel-description room-type-rate-type",
        cancellationClassAndTitle.className
      )}
    >
      <Typography variant="subtitle2" className="text-container">
        {cancellationClassAndTitle.tagText}
      </Typography>
    </Box>
  );
};

export const RoomName = ({
  name,
  count = 1,
}: {
  name?: string;
  count?: number;
}): React.ReactElement | null => {
  if (!name) return null;

  return (
    <Typography variant="body1" className="hotel-description room-type-name">
      {!isCorpTenant(config.TENANT) && (
        <strong>{useMultiroomCountText(count)}</strong>
      )}{" "}
      {name} {isCorpTenant(config.TENANT) && useMultiroomCountText(count)}
    </Typography>
  );
};

export const RoomCapacity = ({
  occupancy,
}: {
  occupancy?: number;
}): React.ReactElement | null => {
  if (!occupancy) {
    return null;
  }
  return (
    <Typography
      variant="body2"
      className="hotel-description room-type-capacity"
    >
      <FontAwesomeIcon icon={faUser} />
      Room fits {occupancy} {occupancy > 1 ? "guests" : "guest"}
    </Typography>
  );
};

export const RoomBedDescription = ({
  description,
}: {
  description?: string;
}): React.ReactElement => (
  <Typography
    variant="body2"
    className="hotel-description room-type-bed-description"
  >
    <FontAwesomeIcon icon={faBed} />
    {description}
  </Typography>
);

export const HotelCancellation = ({
  hotelCfar,
  cancellationPolicy,
  cancellationPolicyV2,
  refundAmount,
}: {
  hotelCfar?: HotelCfarContract;
  cancellationPolicy: HotelCancellationPolicy;
  cancellationPolicyV2: HotelCancellationPolicyV2;
  refundAmount: string;
}) => {
  const cancellationPolicyExperimentEnabled = useExperiment(
    ActiveExperiments.HOTELS_CANCELLATION_POLICY_EXPERIMENT
  );

  const { matchesMobile } = useDeviceTypes();
  const { iconName, primaryText, secondaryText } = getCancellationText({
    cancellationPolicy,
    cancellationPolicyV2,
    hotelCfar,
    refundAmount,
    cancellationPolicyExperimentEnabled,
  });

  if (!primaryText) {
    return null;
  }

  return (
    <Box className={clsx("hotel-cancellation-text", { mobile: matchesMobile })}>
      <Box className="hotel-cancellation-title-line">
        {iconName && (
          <Icon className="cancellation-text-icon" name={iconName} />
        )}
        <Typography variant="body1" className="cancellation-title">
          {primaryText}
        </Typography>
      </Box>
      {secondaryText && (
        <SanitizedHtml
          as={Typography}
          variant="body2"
          className="cancellation-subtitle"
          html={secondaryText}
        />
      )}
    </Box>
  );
};

export const HotelCheckInInstructions = ({
  fromDate,
  untilDate,
  checkInInstructions,
}: {
  fromDate: string;
  untilDate: string;
  checkInInstructions?: LodgingAvailabilityCheckInInstructions;
}) => (
  <Box className={clsx("hotel-check-in-text")}>
    <Typography variant="body1" className="hotel-check-in-title">
      {CHECK_IN_INSTRUCTIONS_TITLE}
    </Typography>
    <SanitizedHtml
      as={Typography}
      variant="body2"
      html={formattedCheckIn(fromDate, checkInInstructions?.checkInTime)}
    />
    <SanitizedHtml
      as={Typography}
      variant="body2"
      html={formattedCheckOut(untilDate, checkInInstructions?.checkOutTime)}
    />
  </Box>
);

export const PremierCollectionBenefits = ({
  isMobile,
}: {
  isMobile?: boolean;
}) => (
  <Box
    className={clsx("trips-premier-collection-benefits-container", {
      mobile: !!isMobile,
    })}
  >
    <Box className="benefits-icon-title-wrapper">
      <Icon name={IconName.StarOutline} />
      <Typography className="benefits-title" variant="body1">
        {PREMIER_COLLECTION_BENEFITS_TITLE}
      </Typography>
    </Box>
    {PREMIER_COLLECTION_BENEFITS_LIST}
  </Box>
);

export const HotelCardRoomTypeContent = ({
  bookedRooms,
  isLuxuryCollection,
}: IHotelShopRoomTypePickerProps): React.ReactElement | null => {
  if (!bookedRooms) {
    return null;
  }

  // we currently only allow booking multiroom with one room type
  const bookedRoom = bookedRooms[0];

  const { count, roomInfo } = bookedRoom;

  return (
    <Box className="hotel-shop-room-type-room-details-list">
      <Typography className="room-type-title" variant="body1">
        {ROOM_TYPE}
      </Typography>
      <RoomName name={roomInfo.name} count={count} />
      <RoomCapacity occupancy={roomInfo.maxAdults} />
      <RoomBedDescription description={roomInfo.beds.description} />
      {isLuxuryCollection && <PremierCollectionBenefits />}
    </Box>
  );
};
